import { ReactNode, useContext, useState } from "react";
import { Loader2, Pause, Volume2 } from "lucide-react";
import { Button } from "../ui/button";
import { supabase } from "@/lib/supabase/client";
import { audioPlayerContext } from "@/contexts/audioPlayerContext";
import { TTS_STORAGE_BUCKET, ttsAudioStoragePath } from "@/lib/tts/ttsAudioPath";

const blobUrlCache = new Map<string, string>();

export type TaskAudioSegmentProps = {
    taskId: number;
    index: number;
    hasAudio: boolean;
    audioTitle?: string;
    children?: ReactNode;
};

/**
 * Wraps one TTS segment of a task body. Reserves left-margin space for the
 * speaker icon via padding (not a negative offset) since the parent
 * container clips overflow. Audio is fetched lazily on click, not prefetched.
 */
export function TaskAudioSegment({ taskId, index, hasAudio, audioTitle, children }: TaskAudioSegmentProps) {
    const { setAudioUrl, setOpen, setTitle, setIsPlaying, isPlaying: isPlayerPlaying, audioUrl: currentAudioUrl } = useContext(audioPlayerContext);
    const [loading, setLoading] = useState(false);

    const path = ttsAudioStoragePath(taskId, index);
    const isThisPlaying = isPlayerPlaying && currentAudioUrl != null && currentAudioUrl === blobUrlCache.get(path);

    async function handleClick() {
        if (isThisPlaying) {
            setIsPlaying(false);
            return;
        }

        const cachedUrl = blobUrlCache.get(path);
        if (cachedUrl) {
            setAudioUrl(cachedUrl);
            setOpen(true);
            setTitle(audioTitle ?? "");
            setIsPlaying(true);
            return;
        }

        setLoading(true);
        const { data, error } = await supabase.storage.from(TTS_STORAGE_BUCKET).download(path);
        setLoading(false);
        if (error || !data) {
            console.error(error);
            return;
        }

        const blobUrl = URL.createObjectURL(data);
        blobUrlCache.set(path, blobUrl);
        setAudioUrl(blobUrl);
        setOpen(true);
        setTitle(audioTitle ?? "");
        setIsPlaying(true);
    }

    return (
        <div className="relative pl-8">
            {hasAudio && (
                <Button
                    type="button"
                    variant="step-current-outline"
                    size="icon"
                    disabled={loading}
                    className="absolute left-0 mt-2 h-6 w-6"
                    onClick={handleClick}
                    aria-label={isThisPlaying ? "Pausa uppläsning" : "Lyssna"}
                >
                    {loading ? <Loader2 className="animate-spin" /> : isThisPlaying ? <Pause /> : <Volume2 />}
                </Button>
            )}
            {children}
        </div>
    );
}
